/**
 * @module features/display-module/types/ddm
 * @description TypeScript domain models, Zod validation schemas, and client-side
 * media size/duration validators (`<= 4 MB` image, `<= 15 MB` video, `<= 15s` / `<= 5s`
 * video duration) for all 7 Digital Display Module (DDM) artifact types (`T001`, `T002`).
 */

import { z } from "zod";

export const DDM_ARTIFACT_TYPES = [
  "notice",
  "birthday",
  "emergency_alert",
  "quote",
  "gallery",
  "custom_banner",
  "event",
] as const;

export type DdmArtifactType = (typeof DDM_ARTIFACT_TYPES)[number];

export type DdmDeliveryStatus =
  | "LIVE"
  | "DEVICE_RECEIVED_UPCOMING"
  | "PENDING_DEVICE_ACK"
  | "UPDATE_PENDING_ACK"
  | "PAUSED"
  | "EXPIRED";

export const MAX_IMAGE_BYTES = 4 * 1024 * 1024; // 4 MB
export const MAX_VIDEO_BYTES = 15 * 1024 * 1024; // 15 MB
export const MAX_VIDEO_DURATION_SECONDS = 15;
export const MAX_QUOTE_VIDEO_DURATION_SECONDS = 5;
export const MAX_DISPLAY_DURATION_SECONDS = 300;
export const MAX_GALLERY_IMAGES = 5;

export interface DdmArtifactTypeMetadata {
  type: DdmArtifactType;
  label: string;
  shortDescription: string;
  titleLabel?: string;
  titleMaxLength?: number;
  bodyLabel?: string;
  bodyMaxLength?: number;
  allowsImage: boolean;
  allowsVideo: boolean;
  requiresMedia: boolean;
  maxVideoDurationSeconds?: number;
}

export const DDM_ARTIFACT_TYPE_META: Record<
  DdmArtifactType,
  DdmArtifactTypeMetadata
> = {
  notice: {
    type: "notice",
    label: "Notice",
    shortDescription: "General announcements with optional image or video (max 15s).",
    titleLabel: "Notice Title",
    titleMaxLength: 30,
    bodyLabel: "Notice Body",
    bodyMaxLength: 150,
    allowsImage: true,
    allowsVideo: true,
    requiresMedia: false,
    maxVideoDurationSeconds: MAX_VIDEO_DURATION_SECONDS,
  },
  birthday: {
    type: "birthday",
    label: "Birthday",
    shortDescription: "Celebratory greeting card with optional portrait photo.",
    titleLabel: "Celebrant Name / Title",
    titleMaxLength: 100,
    bodyLabel: "Birthday Message",
    bodyMaxLength: 250,
    allowsImage: true,
    allowsVideo: false,
    requiresMedia: false,
  },
  emergency_alert: {
    type: "emergency_alert",
    label: "Emergency Alert",
    shortDescription:
      "High-priority override alert that persists on screen until paused or expired.",
    titleLabel: "Alert Title",
    titleMaxLength: 20,
    bodyLabel: "Alert Message",
    bodyMaxLength: 50,
    allowsImage: true,
    allowsVideo: false,
    requiresMedia: false,
  },
  quote: {
    type: "quote",
    label: "Quote",
    shortDescription: "Inspirational quote with optional image or short 5s video loop.",
    titleLabel: "Author / Attribution",
    titleMaxLength: 100,
    bodyLabel: "Quote Text",
    bodyMaxLength: 300,
    allowsImage: true,
    allowsVideo: true,
    requiresMedia: false,
    maxVideoDurationSeconds: MAX_QUOTE_VIDEO_DURATION_SECONDS,
  },
  gallery: {
    type: "gallery",
    label: "Gallery",
    shortDescription: "Multi-photo carousel (1 to 5 images, up to 4 MB each).",
    titleLabel: "Gallery Group Title",
    titleMaxLength: 100,
    allowsImage: true,
    allowsVideo: false,
    requiresMedia: true,
  },
  custom_banner: {
    type: "custom_banner",
    label: "Custom Banner",
    shortDescription: "Full-screen promotional image (max 4 MB) or video (max 15 MB / 15s).",
    allowsImage: true,
    allowsVideo: true,
    requiresMedia: true,
    maxVideoDurationSeconds: MAX_VIDEO_DURATION_SECONDS,
  },
  event: {
    type: "event",
    label: "Event",
    shortDescription: "Scheduled event highlight with event date and optional media.",
    titleLabel: "Event Title",
    titleMaxLength: 100,
    bodyLabel: "Event Description",
    bodyMaxLength: 250,
    allowsImage: true,
    allowsVideo: true,
    requiresMedia: false,
    maxVideoDurationSeconds: MAX_VIDEO_DURATION_SECONDS,
  },
};

export interface AssignedDdmDeviceSummary {
  device_id: string;
  assignment_id: string;
  device_name: string;
  hardware_device_name: string;
  registered_device_id: string;
  client_id: string;
  client_name: string;
  location: string;
}

export interface DdmMediaAsset {
  id: string;
  notification_id: string;
  media_type: "image" | "video";
  original_filename: string;
  storage_key: string;
  public_url: string;
  mime_type: string;
  file_size_bytes: number;
  duration_seconds: number | null;
  created_at: string;
}

export interface DdmContentPayload {
  title?: string;
  body?: string;
  group_title?: string;
  event_date?: string;
  image_url?: string | null;
  image_urls?: string[];
  video_url?: string | null;
  video_duration_seconds?: number | null;
}

export interface DdmNotificationRecord {
  id: string;
  client_id: string;
  client_name: string;
  device_id: string;
  device_name: string;
  registered_device_id: string;
  assignment_id: string;
  artifact_type: DdmArtifactType;
  search_title: string;
  start_timestamp: string;
  end_timestamp: string;
  event_date: string | null;
  duration_of_display: number | null;
  per_cycle_display_count: number;
  pause_emergency_alert: boolean;
  version: number;
  acked_version: number | null;
  acked_at: string | null;
  is_acknowledged: boolean;
  device_received: boolean;
  is_live: boolean;
  delivery_status: DdmDeliveryStatus;
  content_payload: DdmContentPayload;
  remarks: string;
  media_assets: DdmMediaAsset[];
  created_at: string;
  updated_at: string;
}

export interface DdmNotificationListFilterParams {
  page?: number;
  page_size?: number;
  client_id?: string;
  device_id?: string;
  artifact_type?: DdmArtifactType | "all";
  delivery_status?: DdmDeliveryStatus | "all";
  search?: string;
  start_timestamp?: string;
  end_timestamp?: string;
}

export interface DdmDeleteNotificationResponse {
  id: string;
  version: number;
  is_deleted: boolean;
  is_acknowledged: boolean;
  tombstone_queued: boolean;
  deleted_at: string;
}

export interface DdmNotificationFormValues {
  device_id: string;
  artifact_type: DdmArtifactType;
  start_timestamp: string;
  end_timestamp: string;
  duration_of_display: number | null;
  per_cycle_display_count: number;
  pause_emergency_alert: boolean;
  remarks: string;
  title: string;
  body: string;
  group_title: string;
  event_date: string;
  media_mode: "none" | "image" | "video";
  image_url: string;
  image_urls: string[];
  video_url: string;
  video_duration_seconds: number | null;
}

/**
 * Validate a client-selected image file (`<= 4 MB`, JPEG/PNG/WEBP).
 */
export function validateImageFileClientSide(file: File): string | null {
  const allowedMimes = ["image/jpeg", "image/png", "image/webp"];
  if (file.type && !allowedMimes.includes(file.type.toLowerCase())) {
    return `Unsupported image format "${file.name}". Please choose a JPEG, PNG, or WEBP image.`;
  }
  if (file.size > MAX_IMAGE_BYTES) {
    const sizeMb = (file.size / (1024 * 1024)).toFixed(2);
    return `Image "${file.name}" is ${sizeMb} MB, which exceeds the 4 MB maximum limit.`;
  }
  return null;
}

/**
 * Inspect a video file's duration using an offscreen HTML5 `<video preload="metadata">` element.
 */
export function probeVideoDurationSeconds(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    try {
      if (
        typeof document === "undefined" ||
        typeof URL === "undefined" ||
        typeof URL.createObjectURL !== "function"
      ) {
        resolve(null);
        return;
      }
      const video = document.createElement("video");
      video.preload = "metadata";
      const objectUrl = URL.createObjectURL(file);
      const cleanup = () => {
        try {
          URL.revokeObjectURL(objectUrl);
        } catch {
          // ignore cleanup failure
        }
      };
      const timer = setTimeout(() => {
        cleanup();
        resolve(null);
      }, 2500);

      video.onloadedmetadata = () => {
        clearTimeout(timer);
        const duration = Number.isFinite(video.duration) ? video.duration : null;
        cleanup();
        resolve(duration);
      };
      video.onerror = () => {
        clearTimeout(timer);
        cleanup();
        resolve(null);
      };
      video.src = objectUrl;
    } catch {
      resolve(null);
    }
  });
}

/**
 * Validate a client-selected video file (`<= 15 MB`, MP4/WEBM, and `<= 15s` or `<= 5s` for Quote).
 */
export async function validateVideoFileClientSide(
  file: File,
  artifactType: DdmArtifactType
): Promise<{ error: string | null; durationSeconds: number | null }> {
  const meta = DDM_ARTIFACT_TYPE_META[artifactType];
  if (!meta.allowsVideo) {
    return {
      error: `${meta.label} artifacts do not support video attachments.`,
      durationSeconds: null,
    };
  }
  const allowedMimes = ["video/mp4", "video/webm"];
  if (file.type && !allowedMimes.includes(file.type.toLowerCase())) {
    return {
      error: `Unsupported video format "${file.name}". Please choose an MP4 or WEBM video.`,
      durationSeconds: null,
    };
  }
  if (file.size > MAX_VIDEO_BYTES) {
    const sizeMb = (file.size / (1024 * 1024)).toFixed(2);
    return {
      error: `Video "${file.name}" is ${sizeMb} MB, which exceeds the 15 MB maximum limit.`,
      durationSeconds: null,
    };
  }

  const maxDuration =
    artifactType === "quote"
      ? MAX_QUOTE_VIDEO_DURATION_SECONDS
      : MAX_VIDEO_DURATION_SECONDS;
  const durationSeconds = await probeVideoDurationSeconds(file);
  if (durationSeconds !== null && durationSeconds > maxDuration) {
    return {
      error: `Video duration (${durationSeconds.toFixed(1)}s) exceeds the ${maxDuration}-second limit for ${meta.label} artifacts.`,
      durationSeconds,
    };
  }
  return { error: null, durationSeconds };
}

/**
 * Validate `DdmNotificationFormValues` synchronously before submission.
 */
export function validateDdmFormSubmission(
  values: DdmNotificationFormValues,
  options: {
    hasImageFile: boolean;
    hasVideoFile: boolean;
    galleryFilesCount: number;
  }
): string | null {
  if (!values.device_id.trim()) {
    return "Please select an assigned Digital Display Module device.";
  }
  const startMs = Date.parse(values.start_timestamp);
  const endMs = Date.parse(values.end_timestamp);
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
    return "Please provide valid start and end timestamps.";
  }
  if (endMs <= startMs) {
    return "End timestamp must be later than the start timestamp.";
  }

  const type = values.artifact_type;
  const meta = DDM_ARTIFACT_TYPE_META[type];
  const hasImage =
    options.hasImageFile ||
    options.galleryFilesCount > 0 ||
    Boolean(values.image_url.trim()) ||
    values.image_urls.length > 0;
  const hasVideo = options.hasVideoFile || Boolean(values.video_url.trim());

  if (hasImage && hasVideo) {
    return "Both image and video cannot be attached to the same artifact. Choose either Image or Video.";
  }
  if (hasVideo && !meta.allowsVideo) {
    return `${meta.label} artifacts do not allow video attachments.`;
  }

  if (type === "notice") {
    const title = values.title.trim();
    const body = values.body.trim();
    if (!title) return "Notice title is required.";
    if (title.length > 30) return `Notice title cannot exceed 30 characters (currently ${title.length}).`;
    if (!body) return "Notice body is required.";
    if (body.length > 150) return `Notice body cannot exceed 150 characters (currently ${body.length}).`;
  } else if (type === "birthday") {
    if (!values.title.trim()) return "Celebrant name / title is required.";
    if (!values.body.trim()) return "Birthday message is required.";
  } else if (type === "emergency_alert") {
    const title = values.title.trim();
    const body = values.body.trim();
    if (!title) return "Emergency alert title is required.";
    if (title.length > 20)
      return `Emergency alert title cannot exceed 20 characters (currently ${title.length}).`;
    if (!body) return "Emergency alert message is required.";
    if (body.length > 50)
      return `Emergency alert message cannot exceed 50 characters (currently ${body.length}).`;
  } else if (type === "quote") {
    if (!values.title.trim()) return "Quote author / attribution is required.";
    if (!values.body.trim()) return "Quote text is required.";
  } else if (type === "gallery") {
    if (!values.group_title.trim()) return "Gallery group title is required.";
    const totalImages = values.image_urls.length + options.galleryFilesCount + (options.hasImageFile ? 1 : 0);
    if (totalImages < 1) return "Gallery requires at least 1 image (up to 5 images).";
    if (totalImages > MAX_GALLERY_IMAGES)
      return `Gallery allows at most ${MAX_GALLERY_IMAGES} images (currently ${totalImages}).`;
  } else if (type === "custom_banner") {
    if (!(hasImage !== hasVideo)) {
      return "Custom Banner requires either 1 image or 1 video.";
    }
  } else if (type === "event") {
    const title = values.title.trim();
    const body = values.body.trim();
    if (!title) return "Event title is required.";
    if (!body) return "Event description is required.";
    if (body.length > 250)
      return `Event description cannot exceed 250 characters (currently ${body.length}).`;
    if (!values.event_date.trim()) return "Event date (YYYY-MM-DD) is required.";
  }

  if (type !== "emergency_alert") {
    const dur = values.duration_of_display ?? 15;
    const maxCap = hasVideo
      ? type === "quote"
        ? MAX_QUOTE_VIDEO_DURATION_SECONDS
        : MAX_VIDEO_DURATION_SECONDS
      : MAX_DISPLAY_DURATION_SECONDS;
    if (dur < 1 || dur > maxCap) {
      return hasVideo
        ? `When a video is attached to ${meta.label}, display duration cannot exceed ${maxCap} seconds.`
        : `Display duration must be between 1 and ${MAX_DISPLAY_DURATION_SECONDS} seconds.`;
    }
    if (values.per_cycle_display_count < 1) {
      return "Per-cycle display count must be at least 1.";
    }
  }

  return null;
}

export const ddmContentPayloadSchema = z.object({
  title: z.string().optional(),
  body: z.string().optional(),
  group_title: z.string().optional(),
  event_date: z.string().optional(),
  image_url: z.string().nullable().optional(),
  image_urls: z.array(z.string()).optional(),
  video_url: z.string().nullable().optional(),
  video_duration_seconds: z.number().nullable().optional(),
});

